import { useEffect, useState } from 'react';
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DateInput from '../../../components/DateInput/Index';
import { criarDataDeISO } from '../../../global/dataDigitada';
import { useSnackbar } from 'notistack';
import server from '../../../global/server';

const HORAS_MINIMAS = 24;

// Junta a data escolhida no calendario com o horario digitado (HH:mm)
const montarDatetime = (data, hora) => {
    if (!data || !hora) return null;

    const [h, m] = String(hora).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;

    const novo = new Date(data);
    novo.setHours(h, m, 0, 0);
    return novo;
};

const paraHoraTexto = data => {
    const d = new Date(data);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatar = data =>
    data
        ? new Date(data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
        : '—';

/**
 * Troca o horario de um slot ja agendado.
 *
 * O aviso ao conviva e o direito de reembolso sao decididos no servidor; aqui
 * so antecipamos as duas informacoes que mudam a decisao do anfitriao: se o
 * horario ja foi vendido e se ainda da tempo de trocar.
 */
export default ({ open, schedule, onClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState(null);
    const [hora, setHora] = useState('');
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (schedule?.datetime) {
            setData(new Date(schedule.datetime));
            setHora(paraHoraTexto(schedule.datetime));
        } else {
            setData(null);
            setHora('');
        }
    }, [open, schedule?.id, schedule?.datetime]);

    const convivas = Array.isArray(schedule?.Order)
        ? schedule.Order.filter(o => ['CONCLUDED', 'PENDING'].includes(o.status)).length
        : 0;

    const horarioAtual = schedule?.datetime ? new Date(schedule.datetime) : null;
    const horasAteOAtual = horarioAtual ? (horarioAtual.getTime() - Date.now()) / (1000 * 60 * 60) : Infinity;
    const travadoPelaAntecedencia = convivas > 0 && horasAteOAtual < HORAS_MINIMAS;

    const salvar = async () => {
        const novo = montarDatetime(data, hora);

        if (!novo) {
            enqueueSnackbar('Escolha a nova data e o horário', { variant: 'warning' });
            return;
        }

        if (novo.getTime() <= Date.now()) {
            enqueueSnackbar('O novo horário precisa estar no futuro', { variant: 'warning' });
            return;
        }

        if (horarioAtual && novo.getTime() === horarioAtual.getTime()) {
            enqueueSnackbar('O novo horário é igual ao atual', { variant: 'warning' });
            return;
        }

        try {
            setSalvando(true);
            const { data: res } = await server.patch(
                `/profile/meeting/schedule/${schedule.id}/datetime`,
                { datetime: novo.toISOString() },
            );
            enqueueSnackbar(res?.message || 'Horário alterado', { variant: 'success' });
            onSaved?.();
            onClose?.();
        } catch (error) {
            // O servidor e quem conhece as regras (antecedencia, conflitos):
            // mostrar a mensagem dele e mais util do que uma frase generica
            enqueueSnackbar(error.response?.data?.message || 'Erro ao alterar o horário', {
                variant: 'error',
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 600 }}>Alterar horário</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Horário atual: <strong>{formatar(horarioAtual)}</strong>
                    </Typography>

                    {travadoPelaAntecedencia && (
                        <Alert severity="error" sx={{ borderRadius: '12px' }}>
                            Este horário já foi adquirido e falta menos de {HORAS_MINIMAS}h para o encontro.
                            A troca não é mais permitida.
                        </Alert>
                    )}

                    {convivas > 0 && !travadoPelaAntecedencia && (
                        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                            {convivas === 1 ? '1 conviva já adquiriu' : `${convivas} convivas já adquiriram`} este
                            horário. {convivas === 1 ? 'Ele será avisado' : 'Eles serão avisados'} pelo WhatsApp e
                            {convivas === 1 ? ' poderá' : ' poderão'} cancelar com reembolso integral caso não
                            concorde{convivas === 1 ? '' : 'm'} com o novo horário.
                        </Alert>
                    )}

                    <DateInput
                        label="Nova data"
                        value={data}
                        onChange={iso => setData(iso ? criarDataDeISO(iso) : null)}
                        minima={new Date()}
                        rotuloMinima="A data não pode ser no passado"
                    />

                    <TextField
                        label="Novo horário"
                        type="time"
                        size="small"
                        fullWidth
                        value={hora}
                        onChange={e => setHora(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>
                    Cancelar
                </Button>
                <LoadingButton
                    onClick={salvar}
                    loading={salvando}
                    disabled={travadoPelaAntecedencia}
                    variant="contained"
                    sx={{ bgcolor: '#006a61', textTransform: 'none', '&:hover': { bgcolor: '#005a52' } }}
                >
                    Alterar horário
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
